import axios from "axios";
import axiosInstance from "../../services/axiosInstance";

// Helpdesk API. Agent calls go through the CRM's axiosInstance (token + 401
// handling). Customer-facing calls (widget, CSAT) use a separate client so a
// 401 there never logs anybody out or redirects the embed.

const unwrap = (res) => res.data?.data;
const base = "/helpdesk";

export const errorMessage = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message || err?.message || fallback;

export const errorDetails = (err) => err?.response?.data?.details;

const get = (url, params) => axiosInstance.get(`${base}${url}`, { params }).then(unwrap);
const post = (url, body) => axiosInstance.post(`${base}${url}`, body).then(unwrap);
const patch = (url, body) => axiosInstance.patch(`${base}${url}`, body).then(unwrap);
const del = (url) => axiosInstance.delete(`${base}${url}`).then((r) => r.data);

export const hd = {
  meta: () => get("/meta"),
  search: (q) => get("/search", { q }),

  // tickets
  tickets: (params) => axiosInstance.get(`${base}/tickets`, { params }).then((r) => ({ rows: r.data.data, pagination: r.data.pagination })),
  viewCounts: () => get("/tickets/views/counts"),
  bulkTickets: (body) => axiosInstance.post(`${base}/tickets/bulk`, body).then((r) => ({ ...r.data.data, message: r.data.message })),
  savedViews: () => get("/saved-views"),
  createSavedView: (body) => post("/saved-views", body),
  updateSavedView: (id, body) => patch(`/saved-views/${id}`, body),
  deleteSavedView: (id) => del(`/saved-views/${id}`),
  ticket: (id) => get(`/tickets/${id}`),
  createTicket: (body) => post("/tickets", body),
  updateTicket: (id, body) => patch(`/tickets/${id}`, body),
  deleteTicket: (id) => del(`/tickets/${id}`),
  trash: (params) => axiosInstance.get(`${base}/tickets/trash`, { params }).then((r) => ({ rows: r.data.data, pagination: r.data.pagination })),
  restoreTicket: (id) => post(`/tickets/${id}/restore`, {}),
  assign: (id, body) => post(`/tickets/${id}/assign`, body),
  setStatus: (id, body) => post(`/tickets/${id}/status`, body),
  resolve: (id, body) => post(`/tickets/${id}/resolve`, body),
  close: (id, body) => post(`/tickets/${id}/close`, body),
  reopen: (id, body) => post(`/tickets/${id}/reopen`, body),
  merge: (id, body) => post(`/tickets/${id}/merge`, body),
  messages: (id, params) => axiosInstance.get(`${base}/tickets/${id}/messages`, { params }).then((r) => ({ rows: r.data.data, hasMore: r.data.hasMore })),
  reply: (id, body) => post(`/tickets/${id}/reply`, body),
  note: (id, body) => post(`/tickets/${id}/notes`, body),
  applyMacro: (id, cannedId) => axiosInstance.post(`${base}/tickets/${id}/macros/${cannedId}`, {}).then((r) => ({ ticket: r.data.data, message: r.data.message })),
  timeline: (id) => get(`/tickets/${id}/timeline`),
  createTask: (id, body) => post(`/tickets/${id}/tasks`, body),
  updateTask: (taskId, body) => patch(`/tasks/${taskId}`, body),
  myTasks: () => get("/tasks/mine"),

  // AI
  aiSummary: (id) => post(`/tickets/${id}/ai/summary`, {}),
  aiReply: (id, body) => post(`/tickets/${id}/ai/reply`, body || {}),
  aiTriage: (id, body) => post(`/tickets/${id}/ai/triage`, body || {}),
  aiKb: (id) => get(`/tickets/${id}/ai/kb`),
  aiTranslate: (body) => post("/ai/translate", body),
  aiAnalyze: (body) => post("/ai/analyze", body),

  // conversations
  conversations: (params) => axiosInstance.get(`${base}/conversations`, { params }).then((r) => ({ rows: r.data.data, hasMore: r.data.hasMore })),
  conversation: (id) => get(`/conversations/${id}`),
  conversationMessages: (id, params) => axiosInstance.get(`${base}/conversations/${id}/messages`, { params }).then((r) => ({ rows: r.data.data, hasMore: r.data.hasMore })),
  sendChat: (id, body) => post(`/conversations/${id}/messages`, body),
  chatAction: (id, action, body) => post(`/conversations/${id}/${action}`, body || {}),

  // customers
  customer: (contactId) => get(`/customers/${contactId}`),
  updateCustomerProfile: (contactId, body) => patch(`/customers/${contactId}/profile`, body),

  // attachments
  upload: (files, ticketId) => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    if (ticketId) fd.append("ticketId", ticketId);
    return axiosInstance.post(`${base}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(unwrap);
  },
  attachmentUrl: (id) => get(`/attachments/${id}/url`),

  // notifications / presence
  notifications: (params) => axiosInstance.get(`${base}/notifications`, { params }).then((r) => ({ rows: r.data.data, unread: r.data.unread })),
  readNotifications: (ids) => post("/notifications/read", { ids }),
  setPresence: (status) => post("/presence", { status }),

  // knowledge base
  kbSearch: (q, params) => get("/kb/search", { q, ...params }),
  kbImportFaqs: (publish) => axiosInstance.post(`${base}/kb/import-faqs`, { publish }).then((r) => r.data),

  // reports
  report: (name, params) => get(`/reports/${name}`, params),
  reportCsv: (name, params) => axiosInstance.get(`${base}/reports/${name}`, { params: { ...params, format: "csv" }, responseType: "blob" }).then((r) => r.data),

  // settings
  settings: () => get("/settings"),
  updateSettings: (body) => patch("/settings", body),
  rotateWidgetKey: () => post("/settings/widget-key", {}),
  rotatePortalToken: () => post("/settings/portal-token", {}),
  testAiProvider: () => post("/settings/ai/test", {}),
  testWhatsApp: (body) => post("/settings/whatsapp/test", body),
  rotateWhatsAppWebhook: () => post("/settings/whatsapp/webhook-key", {}),
  permissions: () => get("/permissions"),
  templateVariables: () => get("/template-variables"),
  agents: () => get("/agents"),
  updateAgent: (memberId, body) => patch(`/agents/${memberId}`, body),
  automationRuns: (params) => get("/automation-runs", params),
  webhookDeliveries: (id) => get(`/webhooks/${id}/deliveries`),
  testMailbox: (id, body) => post(id ? `/mailboxes/${id}/test` : "/mailboxes/test", body),
  pollMailbox: (id) => post(`/mailboxes/${id}/poll`, {}),
  inboundEmails: (params) => get("/inbound-emails", params),
  audit: (params) => get("/audit", params),
};

/** Generic CRUD for config collections: statuses, priorities, teams, ... */
export const crud = (path) => ({
  list: (params) => get(path, params),
  get: (id) => get(`${path}/${id}`),
  create: (body) => post(path, body),
  update: (id, body) => patch(`${path}/${id}`, body),
  remove: (id) => del(`${path}/${id}`),
});

// ---------------------------------------------------------------------------
// Public (customer) client

const publicClient = axios.create({ baseURL: process.env.REACT_APP_JUSTTAWK_IND_BASE_API_URL });

export const publicApi = {
  widgetConfig: (key, hostOrigin) => publicClient.get(`/public/helpdesk/widget/${encodeURIComponent(key)}/config`, { params: { hostOrigin } }).then(unwrap),
  startSession: (key, body) => publicClient.post(`/public/helpdesk/widget/${encodeURIComponent(key)}/session`, body).then(unwrap),
  messages: (token, after) => publicClient.get("/public/helpdesk/chat/messages", { headers: { "X-Visitor-Token": token }, params: after ? { after } : {} }).then(unwrap),
  send: (token, body) => publicClient.post("/public/helpdesk/chat/messages", body, { headers: { "X-Visitor-Token": token } }).then(unwrap),
  requestAgent: (token) => publicClient.post("/public/helpdesk/chat/agent", {}, { headers: { "X-Visitor-Token": token } }).then(unwrap),
  end: (token) => publicClient.post("/public/helpdesk/chat/end", {}, { headers: { "X-Visitor-Token": token } }).then((r) => r.data),
  upload: (token, files) => {
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    return publicClient.post("/public/helpdesk/chat/attachments", fd, { headers: { "X-Visitor-Token": token, "Content-Type": "multipart/form-data" } }).then(unwrap);
  },
  fileBlob: (token, id) => publicClient.get(`/public/helpdesk/chat/attachments/${id}`, { headers: { "X-Visitor-Token": token }, responseType: "blob" }).then((r) => r.data),
  csat: (token) => publicClient.get(`/public/helpdesk/csat/${encodeURIComponent(token)}`).then(unwrap),
  submitCsat: (token, body) => publicClient.post(`/public/helpdesk/csat/${encodeURIComponent(token)}`, body).then(unwrap),
};

// Customer portal (/support/<slug>). `token` is the portal session from a sign-in link.
const portalPath = (slug, rest = "") => `/public/helpdesk/portal/${encodeURIComponent(slug)}${rest}`;
const bearer = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
export const portalApi = {
  config: (slug, token) => publicClient.get(portalPath(slug), { params: token ? { token } : {} }).then(unwrap),
  articles: (slug, params) => publicClient.get(portalPath(slug, "/articles"), { params }).then(unwrap),
  article: (slug, id) => publicClient.get(portalPath(slug, `/articles/${id}`)).then(unwrap),
  feedback: (slug, id, helpful) => publicClient.post(portalPath(slug, `/articles/${id}/feedback`), { helpful }).then(unwrap),
  createTicket: (slug, payload, files = [], authToken = null) => {
    let reqData;
    const headers = {};
    if (authToken) headers.Authorization = `Bearer ${authToken}`;
    const payloadWithOrigin = {
      origin: typeof window !== "undefined" ? window.location.origin : undefined,
      ...payload,
    };
    if (files && files.length) {
      const fd = new FormData();
      Object.entries(payloadWithOrigin).forEach(([k, v]) => {
        if (typeof v === "object" && v !== null) fd.append(k, JSON.stringify(v));
        else if (v !== undefined && v !== null) fd.append(k, String(v));
      });
      [...files].forEach((f) => fd.append("files", f));
      headers["Content-Type"] = "multipart/form-data";
      reqData = fd;
    } else {
      reqData = payloadWithOrigin;
    }
    return publicClient.post(portalPath(slug, "/tickets"), reqData, { headers }).then(unwrap);
  },
  guestTicket: (slug, token) => publicClient.get(portalPath(slug, `/guest/tickets/${encodeURIComponent(token)}`)).then(unwrap),
  guestReply: (slug, token, content, files = []) => {
    let reqData;
    const headers = {};
    if (files && files.length) {
      const fd = new FormData();
      fd.append("content", content || "");
      [...files].forEach((f) => fd.append("files", f));
      headers["Content-Type"] = "multipart/form-data";
      reqData = fd;
    } else {
      reqData = { content };
    }
    return publicClient.post(portalPath(slug, `/guest/tickets/${encodeURIComponent(token)}/replies`), reqData, { headers }).then(unwrap);
  },
  guestFile: (slug, token, fileId) =>
    publicClient.get(portalPath(slug, `/guest/tickets/${encodeURIComponent(token)}/files/${fileId}`), { responseType: "blob" }).then((r) => r.data),
  login: (slug, { email, password }) => publicClient.post(portalPath(slug, "/auth/login"), { email, password }).then(unwrap),
  register: (slug, { name, email, password }) => publicClient.post(portalPath(slug, "/auth/register"), { name, email, password }).then(unwrap),
  me: (slug, token) => publicClient.get(portalPath(slug, "/auth/me"), bearer(token)).then(unwrap),
  logout: (slug) => publicClient.post(portalPath(slug, "/auth/logout")).then(unwrap),
  forgotPassword: (slug, email) =>
    publicClient
      .post(portalPath(slug, "/auth/forgot-password"), {
        email,
        origin: typeof window !== "undefined" ? window.location.origin : undefined,
      })
      .then(unwrap),
  resetPassword: (slug, { token, password }) => publicClient.post(portalPath(slug, "/auth/reset-password"), { token, password }).then(unwrap),
  sendOtp: (slug, email) =>
    publicClient
      .post(portalPath(slug, "/auth/send-otp"), {
        email,
        origin: typeof window !== "undefined" ? window.location.origin : undefined,
      })
      .then(unwrap),
  verifyOtp: (slug, { email, otp }) => publicClient.post(portalPath(slug, "/auth/verify-otp"), { email, otp }).then(unwrap),
  requestLink: (slug, email) =>
    publicClient
      .post(portalPath(slug, "/auth/link"), {
        email,
        origin: typeof window !== "undefined" ? window.location.origin : undefined,
      })
      .then(unwrap),
  session: (slug, token) => publicClient.post(portalPath(slug, "/auth/session"), { token }).then(unwrap),
  myTickets: (slug, token) => publicClient.get(portalPath(slug, "/my/tickets"), bearer(token)).then((r) => ({ rows: r.data.data, email: r.data.email })),
  myTicket: (slug, token, key) => publicClient.get(portalPath(slug, `/my/tickets/${encodeURIComponent(key)}`), bearer(token)).then(unwrap),
  file: (slug, token, key, fileId) => publicClient.get(portalPath(slug, `/my/tickets/${encodeURIComponent(key)}/files/${fileId}`), { ...bearer(token), responseType: "blob" }).then((r) => r.data),
  reply: (slug, token, key, content) => publicClient.post(portalPath(slug, `/my/tickets/${encodeURIComponent(key)}/replies`), { content }, bearer(token)).then(unwrap),
};

export const apiBase = () => String(process.env.REACT_APP_JUSTTAWK_IND_BASE_API_URL || "").replace(/\/+$/, "");

/** Opens an attachment through a short-lived signed URL (auth stays in headers). */
export const openAttachment = async (id, inline = true) => {
  const { url } = await hd.attachmentUrl(id);
  window.open(`${apiBase()}${url}${inline ? "?inline=1" : ""}`, "_blank", "noopener,noreferrer");
};
